import React, {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EventType,
  RouteStopEventFragment,
  RouteStopFragment,
  SequenceInput,
  useUpdateRouteStopsMutation,
} from "../../../api/nachfahrbarkeit-api/generated";
import { sortBy } from "lodash";
import { getDistance } from "../../../utils/coordinates";
import { v4, validate } from "uuid";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useUnsavedDataContext } from "../../../components/unsaved-data-prompt/unsaved-data-context";

interface EditRouteStopsContextType {
  stops: RouteStopFragment[];
  isInEditMode: boolean;
  setIsInEditMode: (isInEditMode: boolean) => void;
  moveStop: (stopId: string, lat: number, lng: number) => void;
  deleteStop: (stopId: string) => void;
  addStop: (lat: number, lng: number) => void;
  updateStop: (stopId: string, info: string | undefined, backwards: boolean) => void;
  saveChanges: () => void;
  cancelEditing: () => void;
  isUpdating: boolean;
}

interface EditRouteStopFragment extends RouteStopFragment {
  dirty: boolean;
}

export const EditRouteStopsContext = createContext<EditRouteStopsContextType>({
  stops: [],
  isInEditMode: false,
  setIsInEditMode: () => null,
  moveStop: () => null,
  deleteStop: () => null,
  addStop: () => null,
  updateStop: () => null,
  saveChanges: () => null,
  cancelEditing: () => null,
  isUpdating: false,
});

interface EditRouteStopsContextProviderProps {
  routeId: string;
  stops: RouteStopFragment[];
}

export const EditRouteStopsContextProvider: FunctionComponent<PropsWithChildren<EditRouteStopsContextProviderProps>> = (
  props,
) => {
  const { children } = props;
  const [stopsToEdit, setStopsToEdit] = useState<EditRouteStopFragment[]>([]);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [updateTriggered, setUpdateTriggered] = useState(false);
  const { t } = useTranslation();
  const { setHasUnsavedData, setUnsavedDataTitle, setUnsavedDataContent, hasUnsavedData } = useUnsavedDataContext();

  const onUpdateComplete = useCallback(() => {
    setIsInEditMode(false);
    setHasUnsavedData(false);
    toast.success(t("routes_management.update.success"));
  }, [t, setHasUnsavedData]);
  const onUpdateError = useCallback(() => {
    toast.error(t("routes_management.update.error"));
  }, [t]);

  const [updateRouteStopsMutation, { loading: updateMutationLoading }] = useUpdateRouteStopsMutation({
    onCompleted: onUpdateComplete,
    onError: onUpdateError,
  });

  const setUnsavedData = useCallback(() => {
    if (hasUnsavedData) {
      return;
    }

    setUnsavedDataTitle(t("routes_management.update.unsaved_data_prompt.title"));
    setUnsavedDataContent(t("routes_management.update.unsaved_data_prompt.content"));
    setHasUnsavedData(true);
  }, [setHasUnsavedData, setUnsavedDataContent, setUnsavedDataTitle, hasUnsavedData, t]);

  useEffect(() => {
    setStopsToEdit(props.stops.map((stop) => ({ ...stop, dirty: false })));
  }, [props.stops]);

  const moveStop = useCallback(
    (stopId: string, lat: number, lng: number) => {
      setStopsToEdit(
        stopsToEdit.map((stop) => {
          if (stop.id === stopId) {
            return { ...stop, latitude: lat, longitude: lng, dirty: true };
          }
          return stop;
        }),
      );
      setUnsavedData();
    },
    [stopsToEdit, setUnsavedData],
  );

  const deleteStop = useCallback(
    (stopId: string) => {
      const stop = stopsToEdit.find((s) => s.id === stopId);

      if (!stop) {
        return;
      }

      setStopsToEdit(
        stopsToEdit
          .filter((s) => s.id !== stopId)
          .map((s) =>
            s.sequence_number > stop.sequence_number ? { ...s, sequence_number: s.sequence_number - 1 } : s,
          ),
      );
      setUnsavedData();
    },
    [stopsToEdit, setUnsavedData],
  );

  const addStop = useCallback(
    (lat: number, lng: number) => {
      if (stopsToEdit.length <= 1) {
        setStopsToEdit([
          {
            id: v4(),
            latitude: lat,
            longitude: lng,
            sequence_number: stopsToEdit.length,
            events: [],
            dirty: true,
          },
        ]);
        return;
      }

      const nearestStops = sortBy(stopsToEdit, (stop) => getDistance(stop.latitude, stop.longitude, lat, lng)).slice(
        0,
        2,
      );
      let nearestStop = nearestStops[0];

      if (nearestStop.sequence_number - nearestStops[1].sequence_number === 1) {
        nearestStop = nearestStops[1];
      }

      setStopsToEdit(
        [
          ...stopsToEdit.map((stop) =>
            stop.sequence_number > nearestStop.sequence_number
              ? { ...stop, sequence_number: stop.sequence_number + 1 }
              : stop,
          ),
          {
            id: v4(),
            latitude: lat,
            longitude: lng,
            sequence_number: nearestStop.sequence_number + 1,
            events: [],
            dirty: true,
          },
        ].sort((a, b) => a.sequence_number - b.sequence_number),
      );
      setUnsavedData();
    },
    [stopsToEdit, setUnsavedData],
  );

  const updateStop = useCallback(
    (stopId: string, info: string | undefined, backwards: boolean) => {
      setStopsToEdit(
        stopsToEdit.map((stop) => {
          if (stop.id === stopId) {
            let backwardsEvent = stop.events?.find((event) => event.event_type === EventType.backwards);
            let infoEvent = stop.events?.find((event) => event.event_type === EventType.bulk && event.info);

            if (backwards && !backwardsEvent) {
              backwardsEvent = {
                id: v4(),
                event_type: EventType.backwards,
                images: [],
              };
            } else if (!backwards && backwardsEvent) {
              backwardsEvent = undefined;
            }

            if (info && !infoEvent) {
              infoEvent = {
                id: v4(),
                event_type: EventType.bulk,
                info,
                images: [],
              };
            } else if (!info && infoEvent) {
              infoEvent = undefined;
            } else if (info && infoEvent) {
              infoEvent.info = info;
            }

            return {
              ...stop,
              events: [backwardsEvent, infoEvent].filter((event) => event) as RouteStopEventFragment[],
              dirty: true,
            };
          }
          return stop;
        }),
      );
      setUnsavedData();
    },
    [stopsToEdit, setUnsavedData],
  );

  const cancelEditing = useCallback(() => {
    setStopsToEdit(props.stops.map((stop) => ({ ...stop, dirty: false })));
    setIsInEditMode(false);
    setHasUnsavedData(false);
  }, [props.stops, setHasUnsavedData]);

  const saveChanges = useCallback(() => {
    setUpdateTriggered(true);
    const deletedStopIds = props.stops
      .filter((stop) => !stopsToEdit.some((s) => s.id === stop.id))
      .map((stop) => stop.id);
    const newStops = stopsToEdit.filter((stop) => !props.stops.some((s) => s.id === stop.id));
    const updatedStops = stopsToEdit.filter((stop) => props.stops.some((s) => s.id === stop.id && stop.dirty));

    const newSequence: SequenceInput[] =
      newStops.length || deletedStopIds.length
        ? stopsToEdit
            .filter((stop) => props.stops.some((s) => s.id === stop.id))
            .map((stop) => ({
              stopId: stop.id,
              sequenceNumber: stop.sequence_number,
            }))
        : [];

    updateRouteStopsMutation({
      variables: {
        routeId: props.routeId,
        deletedStopIds,
        newStops: newStops.map((stop) => ({
          latitude: stop.latitude,
          longitude: stop.longitude,
          sequenceNumber: stop.sequence_number,
          events:
            stop.events?.map((event) => ({
              type: event.event_type,
              info: event.info,
            })) || [],
        })),
        updatedStops: updatedStops.map((stop) => ({
          id: stop.id,
          latitude: stop.latitude,
          longitude: stop.longitude,
          sequenceNumber: stop.sequence_number,
          events:
            stop.events?.map((event) => ({
              id: !validate(event.id) ? event.id : undefined,
              type: event.event_type,
              info: event.info,
            })) || [],
        })),
        newSequence,
      },
    });
    setUpdateTriggered(false);
  }, [stopsToEdit, props.stops, props.routeId, updateRouteStopsMutation]);

  const stops = useMemo(() => (isInEditMode ? stopsToEdit : props.stops), [stopsToEdit, props.stops, isInEditMode]);

  return (
    <EditRouteStopsContext.Provider
      value={{
        stops,
        isInEditMode,
        setIsInEditMode,
        moveStop,
        deleteStop,
        addStop,
        updateStop,
        saveChanges,
        cancelEditing,
        isUpdating: updateMutationLoading || updateTriggered,
      }}
    >
      {children}
    </EditRouteStopsContext.Provider>
  );
};
